<template>
  <div class="date-range-selector">
    <v-select
      v-model="selectedId"
      :items="presets"
      item-text="label"
      item-value="id"
      outlined
      hide-details
      label="Date range"
      prepend-inner-icon="mdi-calendar-range"
    />

    <v-expand-transition>
      <div v-if="selectedId === 'custom'" class="date-range-selector__custom mt-4">
        <v-text-field
          v-model="customStart"
          type="date"
          outlined
          hide-details
          label="Start date"
          @change="applyCustomRange"
        />
        <v-text-field
          v-model="customEnd"
          type="date"
          outlined
          hide-details
          label="End date"
          @change="applyCustomRange"
        />
      </div>
    </v-expand-transition>

    <div v-if="selectedRange.start && selectedRange.end" class="admin-muted text-caption mt-2">
      {{ formattedRange }}
    </div>
  </div>
</template>

<script>
export default {
  name: "DateRangeSelector",
  props: {
    value: { type: Object, default: () => ({}) },
    defaultRange: { type: String, default: "monthToDate" },
  },
  data: () => ({
    selectedId: "monthToDate",
    customStart: "",
    customEnd: "",
    selectedRange: {},
  }),
  computed: {
    weekStart() {
      const settings = this.$store.getters["integration/modules"];
      const configured = settings && settings.terminalregister
        && settings.terminalregister.IntegrationSettings
        && settings.terminalregister.IntegrationSettings.metadata
        && settings.terminalregister.IntegrationSettings.metadata.weekStartOf;
      return configured === "Sunday" ? "week" : "isoWeek";
    },
    presets() {
      const today = this.$moment();
      const rollingRange = (days) => ({
        start: today.clone().subtract(days - 1, "days").startOf("day").toISOString(),
        end: today.clone().endOf("day").toISOString(),
      });
      return [
        { id: "today", label: "Today", start: today.clone().startOf("day").toISOString(), end: today.clone().endOf("day").toISOString() },
        { id: "yesterday", label: "Yesterday", start: today.clone().subtract(1, "day").startOf("day").toISOString(), end: today.clone().subtract(1, "day").endOf("day").toISOString() },
        { id: "thisWeek", label: "This Week", start: today.clone().startOf(this.weekStart).toISOString(), end: today.clone().endOf(this.weekStart).toISOString() },
        { id: "lastWeek", label: "Last Week", start: today.clone().subtract(1, "week").startOf(this.weekStart).toISOString(), end: today.clone().subtract(1, "week").endOf(this.weekStart).toISOString() },
        { id: "thisMonth", label: "This Month", start: today.clone().startOf("month").toISOString(), end: today.clone().endOf("month").toISOString() },
        { id: "monthToDate", label: "Month to Date", start: today.clone().startOf("month").toISOString(), end: today.clone().endOf("day").toISOString() },
        { id: "lastMonth", label: "Last Month", start: today.clone().subtract(1, "month").startOf("month").toISOString(), end: today.clone().subtract(1, "month").endOf("month").toISOString() },
        { id: "thisYear", label: "This Year", start: today.clone().startOf("year").toISOString(), end: today.clone().endOf("year").toISOString() },
        { id: "yearToDate", label: "Year to Date", start: today.clone().startOf("year").toISOString(), end: today.clone().endOf("day").toISOString() },
        { id: "lastYear", label: "Last Year", start: today.clone().subtract(1, "year").startOf("year").toISOString(), end: today.clone().subtract(1, "year").endOf("year").toISOString() },
        { id: "last7Days", label: "Last 7 Days", ...rollingRange(7) },
        { id: "last10Days", label: "Last 10 Days", ...rollingRange(10) },
        { id: "last30Days", label: "Last 30 Days", ...rollingRange(30) },
        { id: "last90Days", label: "Last 90 Days", ...rollingRange(90) },
        { id: "last365Days", label: "Last 365 Days", ...rollingRange(365) },
        { id: "custom", label: "Custom Range" },
      ];
    },
    formattedRange() {
      return `${this.$moment(this.selectedRange.start).format("MMM D, YYYY")} – ${this.$moment(this.selectedRange.end).format("MMM D, YYYY")}`;
    },
  },
  watch: {
    selectedId(id) {
      if (id === "custom") {
        if (!this.customStart) this.customStart = this.$moment(this.selectedRange.start).format("YYYY-MM-DD");
        if (!this.customEnd) this.customEnd = this.$moment(this.selectedRange.end).format("YYYY-MM-DD");
        this.applyCustomRange();
        return;
      }
      this.applyPreset(id);
    },
  },
  created() {
    const suppliedId = this.value && this.value.id;
    this.selectedId = this.presets.some((preset) => preset.id === suppliedId)
      ? suppliedId
      : this.defaultRange;
    if (this.selectedId === "custom" && this.value.start && this.value.end) {
      this.customStart = this.$moment(this.value.start).format("YYYY-MM-DD");
      this.customEnd = this.$moment(this.value.end).format("YYYY-MM-DD");
      this.applyCustomRange();
    } else {
      this.applyPreset(this.selectedId);
    }
  },
  methods: {
    applyPreset(id) {
      const preset = this.presets.find((item) => item.id === id);
      if (!preset || !preset.start || !preset.end) return;
      this.publish({ id: preset.id, label: preset.label, start: preset.start, end: preset.end });
    },
    applyCustomRange() {
      if (!this.customStart || !this.customEnd) return;
      this.publish({
        id: "custom",
        label: "Custom Range",
        start: this.$moment(this.customStart, "YYYY-MM-DD", true).startOf("day").toISOString(),
        end: this.$moment(this.customEnd, "YYYY-MM-DD", true).endOf("day").toISOString(),
      });
    },
    publish(range) {
      this.selectedRange = range;
      this.$emit("input", range);
    },
  },
};
</script>

<style scoped>
.date-range-selector__custom {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 520px) {
  .date-range-selector__custom { grid-template-columns: 1fr; }
}
</style>
